/**
 * Tier colours (PRD §12.6: restrained colour, tier colours only, WCAG AA). Two ramps: one for UI
 * chips on the panel background, one for map circles on the light Positron basemap. Contrast was
 * checked against #f4f1e6 (light) and #1a1f16 (dark) panels: every text-on-chip pairing is ≥4.5:1.
 */

export const TIER_LABEL: Record<string, string> = {
  "1": "Tier 1",
  "2": "Tier 2",
  "3": "Tier 3",
  x: "Unscored",
};

export const TIER_ORDER = ["1", "2", "3", "x"] as const;

/** Map circle fills, tuned for the light Positron basemap. */
export const TIER_MAP_COLOR: Record<string, string> = {
  "1": "#b8741a",
  "2": "#4f7f3e",
  "3": "#3e6b93",
  x: "#7c7f76",
};

/** Chip backgrounds; text uses var(--ink) on these. */
export const TIER_CHIP_COLOR: Record<string, string> = {
  "1": "#d9a441",
  "2": "#8bb278",
  "3": "#7fa2c4",
  x: "#a3a69b",
};

export const CONTACT_LABEL: Record<string, string> = {
  none: "No contact",
  named: "Named contact",
  verified: "Verified email",
};

export const COOLING_LABEL: Record<string, string> = {
  none: "No inlet cooling",
  evap: "Evaporative media",
  fog: "Fogging",
  wet: "Wet compression",
  chiller: "Chiller",
  mixed: "Mixed",
  unknown: "Unknown",
};

export const COOLING_TYPE_LABEL: Record<string, string> = {
  none: "None",
  evaporative_media: "Evaporative media",
  fogging_downstream_filters: "Fogging, downstream of filters",
  fogging_upstream_filters: "Fogging, upstream of filters",
  wet_compression: "Wet compression",
  chiller: "Chiller",
  unknown: "Unknown",
};

export const FRAME_LABEL: Record<string, string> = {
  aero: "Aeroderivative",
  heavy_E: "Heavy E-class",
  heavy_F: "Heavy F-class",
  heavy_H: "Heavy H-class",
  small_industrial: "Small industrial",
  recip: "Reciprocating",
  unknown: "Unknown",
};

export const SEGMENT_LABEL: Record<string, string> = {
  A: "A · Grid GT",
  B: "B · Recips",
  C: "C · Data centers",
  D: "D · Planned GT",
};

export const SECTOR_LABEL: Record<string, string> = {
  "1": "Electric utility",
  "2": "IPP non-CHP",
  "3": "IPP CHP",
  "4": "Commercial non-CHP",
  "5": "Commercial CHP",
  "6": "Industrial non-CHP",
  "7": "Industrial CHP",
};

export const SITE_TYPE_LABEL: Record<string, string> = {
  simple_cycle: "Simple cycle",
  combined_cycle: "Combined cycle",
  cogen: "Cogeneration",
  mixed: "Mixed",
  recip: "Reciprocating",
  data_center: "Data center",
  unknown: "Unknown",
};

export const AIS_LABEL: Record<string, string> = {
  weather_hood_big: "Big weather hoods",
  weather_hood_small: "Small weather hoods",
  table_filter_house: "Table filter house",
  vertical_filter_house: "Vertical filter house",
  trent_style: "Trent style",
  unknown: "Unknown",
};

export const PRIME_MOVER_LABEL: Record<string, string> = {
  GT: "GT · simple-cycle combustion turbine",
  CT: "CT · combined-cycle combustion turbine",
  CS: "CS · single-shaft combined cycle",
  CA: "CA · combined-cycle steam part",
  IC: "IC · reciprocating engine",
  ST: "ST · steam turbine",
  OT: "OT · other",
};

export const UNIT_STATUS_LABEL: Record<string, string> = {
  OP: "Operating",
  SB: "Standby",
  OA: "Out of service, returned this year",
  OS: "Out of service, not expected back",
  SC: "Cold standby",
  RE: "Retired",
  P: "Planned",
  L: "Regulatory approvals pending",
  T: "Regulatory approvals received",
  U: "Under construction, <50 %",
  V: "Under construction, >50 %",
  TS: "Construction complete, testing",
  CO: "New unit under construction",
  CN: "Cancelled",
  IP: "Planned, indefinitely postponed",
  OT: "Other",
  unknown: "Unknown",
};

export function label(map: Record<string, string>, key: string | null | undefined): string {
  if (key === null || key === undefined) return "—";
  return map[key] ?? key;
}
